import { useEffect, useRef, useState } from 'react';
import { FiCalendar } from 'react-icons/fi';
import SimpleCalendar from '../atoms/SimpleCalendar';

interface DateFieldProps {
    label: string;
    // Valor no formato ISO (yyyy-mm-dd), como o backend espera.
    value: string;
    onChange: (isoDate: string) => void;
    errorMessage?: string;
    disabled?: boolean;
}

const isoToDisplay = (iso: string): string => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return '';
    return `${d}/${m}/${y}`;
};

const displayToIso = (display: string): string => {
    const digits = display.replace(/\D/g, '');
    if (digits.length !== 8) return '';
    const d = digits.slice(0, 2);
    const m = digits.slice(2, 4);
    const y = digits.slice(4, 8);
    const day = Number(d);
    const month = Number(m);
    const year = Number(y);
    // Valida a data de verdade: o JS faz roll-over (31/02 vira 03/03), então se o Date
    // construído não devolver os mesmos dia/mês/ano, a data não existe.
    const date = new Date(year, month - 1, day);
    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ) {
        return '';
    }
    return `${y}-${m}-${d}`;
};

// Aplica a máscara dd/mm/aaaa enquanto digita.
const maskInput = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)];
    return parts.filter(Boolean).join('/');
};

const isoToDate = (iso: string): Date | null => {
    if (!iso) return null;
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

const dateToIso = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const DateField = ({ label, value, onChange, errorMessage, disabled = false }: DateFieldProps) => {
    const [text, setText] = useState(isoToDisplay(value));
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Mantém o texto em sincronia quando o valor muda por fora (ex.: seleção no calendário).
    useEffect(() => {
        setText(isoToDisplay(value));
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleTyping = (raw: string) => {
        const masked = maskInput(raw);
        setText(masked);
        const iso = displayToIso(masked);
        // Só propaga quando a data está completa e válida; parcial não zera o valor.
        if (iso) onChange(iso);
        if (masked === '') onChange('');
    };

    const handlePick = (date: Date) => {
        onChange(dateToIso(date));
        setIsOpen(false);
    };

    const borderColor = errorMessage
        ? 'border-red-500 dark:border-red-400'
        : 'border-gray-300 dark:border-slate-600';

    return (
        <div className="relative" ref={containerRef}>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                {label}
            </label>
            <div className={`flex items-center rounded-md border ${borderColor} bg-white dark:bg-slate-800 focus-within:border-blue-600 dark:focus-within:border-blue-400`}>
                <input
                    type="text"
                    inputMode="numeric"
                    placeholder="dd/mm/aaaa"
                    value={text}
                    disabled={disabled}
                    onChange={(e) => handleTyping(e.target.value)}
                    className="flex-1 bg-transparent px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none disabled:opacity-50"
                />
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setIsOpen((o) => !o)}
                    className="px-3 py-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50"
                    aria-label="Abrir calendário"
                >
                    <FiCalendar size={16} />
                </button>
            </div>

            {errorMessage && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errorMessage}</p>}

            {isOpen && (
                <div className="absolute z-30 mt-2 left-0">
                    <SimpleCalendar selectedDate={isoToDate(value)} onDateChange={handlePick} />
                </div>
            )}
        </div>
    );
};

export default DateField;
